import {
    Add,
    ArrowDropDown,
    ArrowDropUp,
    Close,
    Delete,
    Download,
    Edit,
    ExpandMore,
    MoreVert,
    PushPin,
    PushPinOutlined,
    Visibility,
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    OutlinedInput,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CUSTOOMER_INVOICE_STATUS_COLOR } from '../../App';
import CommonDialog from '../../common/CommonDialog';
import { deleteFinalizedInvoice, deleteInvoice, invoiceListing, sendInvoice } from '../../redux/Invoice/action';
import { businessInvoicesKeys } from '../../services/business/invoices/invoices.hooks';
import { reviewRequestKeys } from '../../services/business/reviewRequest/reviewRequest.hooks.js';
import useInvoiceStore from '../../store/useInvoiceStore';
import useRecordPaymentStore from '../../store/UseRecordPaymentStore';
import { CHIP_COLOR, INVOICE_PAYMENT_STATUS, USER_TIMEZONE } from '../../utility/Constant';
import { NumericEngine } from '../../utility/helpers/numericEngine.js';
import ResendSvg from '../../utility/Svg/ResendSvg';
import ManageColumnsMenu from '../modal/Managecolumnsmenu.jsx';
import SelectVehicleModal from '../modal/SelectVehicleModal';
import AccountsReceivableReportModel from './AccountsReceivableReportModel';
import ExportReportModel from './ExportReportModel';
import { downloadPdfFromUrl } from '../../utility/helpers/fileDownload';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
};

const styles = {
    menuPadding: {
        padding: '0',
        width: 'calc(100% + 10px)!important',
    },
    formControlLabel: {
        padding: '6px 16px',
        marginRight: '0!important',
        width: '100%',
    },
};

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && (
                <Box sx={{ pt: 4 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function formatInvoiceVehicleDisplay(vehicle) {
    if (!vehicle) return '—';
    const year = vehicle.year != null && vehicle.year !== '' ? String(vehicle.year) : '';
    const make = vehicle.vehicleMake?.name ?? '';
    const model = vehicle.vehicleModel?.name ?? '';
    const parts = [year, make, model].filter(Boolean);
    return parts.length ? parts.join(' ') : '—';
}

function formatInvoiceLicensePlateDisplay(vehicle) {
    if (!vehicle?.licenseNumber) return '—';
    return vehicle?.provinceCode ? `${vehicle.provinceCode} ${vehicle.licenseNumber}` : String(vehicle.licenseNumber);
}

/** 7 data columns share remaining width; last column is fixed 48px (actions). */
const INVOICE_LIST_TABLE_SX = { tableLayout: 'auto', width: '100%' };

const INVOICE_LIST_ACTION_COL = { width: 48, minWidth: 48, maxWidth: 48 };
const INVOICE_LIST_COL = {}; // no width — browser decides
const INVOICE_LIST_BODY_CELL = { whiteSpace: 'nowrap' }; // prevents awkward wrapping

const Invoice = () => {
    const {
        isPinned,
        setPinned,
        invoicePaymentStatus,
        setInvoicePaymentStatus,
        resetInvoiceStore,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        search,
        setSearch,
        tabValue: value,
        setTabValue: setValue,
        dateData,
        setDateData,
        selectDate,
        setSelectDate,
        yearData,
        setYearData,
        makeData,
        setMakeData,
        modelData,
        setModelData,
        licenseNumber,
        setLicenseNumber,
        fleetUnitNumber,
        setFleetUnitNumber,
        provinceId,
        setProvinceId,
        vinFilter,
        setVinFilter,
        sortTypeDate,
        setSortTypeDate: setsortTypeDate,
        sortByDate,
        setSortByDate: setsortByDate,
        sortTypeName,
        setSortTypeName: setsortTypeName,
        sortTypeTotal,
        setSortTypeTotal: setsortTypeTotal,
        showInvoiceLicensePlateColumn,
        setShowInvoiceLicensePlateColumn,
        showInvoiceUnitColumn,
        setShowInvoiceUnitColumn,
        resetInvoiceTableColumns,
    } = useInvoiceStore();

    const location = useLocation();

    useEffect(() => {
        const prevPath = sessionStorage.getItem('prevPath');
        const isInternal = prevPath && prevPath.startsWith('/invoice');
        if (!isInternal) {
            resetInvoiceStore();
        }
        sessionStorage.setItem('prevPath', location.pathname);
    }, [location.pathname]);
    const queryClient = useQueryClient();
    const [isDeleteModal, setIsDeleteModal] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);

    const { recordPaymentParams, setCustomerDetail, setRecordPaymentParams } = useRecordPaymentStore();
    const [invoiceData, setInvoiceData] = React.useState([]);
    const [showLoader, setShowLoader] = React.useState(false);
    const dispatch = useDispatch();

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const index = [0, 1, 2, 3];
    const nameArray = window.location.pathname.split('/');
    const name = nameArray[nameArray.length - 1];

    const [anchorEl, setAnchorEl] = useState(null);
    const [anchorElStatus, setAnchorElStatus] = useState(null);
    const openStatus = Boolean(anchorElStatus);
    const [openExportModel, setOpenExportModel] = useState(false);
    const [openAccountExportModel, setOpenAccountExportModel] = useState(false);

    const openExport = Boolean(anchorEl);

    const [anchorElDropdown, setAnchorElDropdown] = useState(null);
    const [invoiceColumnAnchorEl, setInvoiceColumnAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState({});

    const openDropdown = Boolean(anchorElDropdown);
    const openInvoiceColumnSettings = Boolean(invoiceColumnAnchorEl);

    const handleInvoiceColumnSettingsClick = (event) => {
        setInvoiceColumnAnchorEl(event.currentTarget);
    };

    const handleInvoiceColumnSettingsClose = () => {
        setInvoiceColumnAnchorEl(null);
    };

    const handleMenuOpen = (event, row) => {
        setAnchorElDropdown(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setAnchorElDropdown(null);
        // setSelectedRow(null);
    };

    // useEffect(() => {
    //     if (localStorage.getItem('searchValue')) {
    //         console.log('🚀 ~ Invoice ~ localStorage:', localStorage.getItem('searchValue'));
    //         onChange(localStorage.getItem('searchValue'), makeData, modelData, yearData);
    //     }
    // }, []);

    const handleInvoicesClick = () => {
        setOpenExportModel(true);
        handleCloseExport();
    };

    const handleClickExport = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseExport = () => {
        setAnchorEl(null);
    };

    const clickDownload = async (invoiceData) => {
        await downloadPdfFromUrl(invoiceData?.pdfUrl, `${invoiceData?.invoiceNumber || 'invoice'}.pdf`);
    };

    const getStatusButtonText = () => {
        const selectedCount = invoicePaymentStatus?.length || 0;
        if (selectedCount > 0) {
            return `${selectedCount} Status`;
        }
        return 'STATUS';
    };

    const invoiceListingDataParams = {
        page: page,
        rowsPerPage: rowsPerPage,
        currentPageStatus: name === 'sents' ? 'sent' : name === 'drafts' ? 'draft' : name === 'finalized' ? 'finalized' : 'all',
        // sortTypeName: sortTypeName ? sortTypeName : sortTypeName ? sortTypeName : sortTypeTotal ? sortTypeTotal : 'DSC',
        sortTypeName: sortTypeName || sortTypeDate || sortTypeTotal || 'DSC',
        sortByDate: sortByDate,
        licenseNumber: licenseNumber,
        fleetUnitNumber: fleetUnitNumber,
        provinceId: provinceId,
        vinFilter: vinFilter,
        yearData: yearData,
        makeData: makeData,
        modelData: modelData,
        dateData: dateData,
        search: search,
        paymentStatus: invoicePaymentStatus,
    };
    console.log('🚀 ~ Invoice ~ invoiceListingDataParams:', invoiceListingDataParams);

    const handleChange = (event, newValue) => {
        setInvoiceData({});
        setValue(newValue);

        const updatingParams = { ...invoiceListingDataParams };
        updatingParams.page = 1;
        setPage(1);

        switch (newValue) {
            case 0:
                updatingParams.currentPageStatus = 'all';
                invoiceDetailsData(updatingParams);
                navigate('/invoice/all');
                break;
            case 1:
                updatingParams.currentPageStatus = 'finalized';
                invoiceDetailsData(updatingParams);
                navigate('/invoice/finalized');
                break;
            case 2:
                updatingParams.currentPageStatus = 'sent';
                invoiceDetailsData(updatingParams);
                navigate('/invoice/sents');
                break;
            case 3:
                updatingParams.currentPageStatus = 'draft';
                invoiceDetailsData(updatingParams);
                navigate('/invoice/drafts');
                break;
            default:
                updatingParams.currentPageStatus = 'all';
                invoiceDetailsData(updatingParams);
                navigate('/invoice/all');
        }
    };

    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleVehicleCancel = () => {
        // Cancel should restore whatever was applied before opening (store unchanged)
        setOpen(false);
    };
    const handleVehicleApply = (next) => {
        setYearData(next?.yearData ?? '');
        setMakeData(next?.makeData ?? '');
        setModelData(next?.modelData ?? '');
        setLicenseNumber(next?.licenseNumber ?? '');
        setFleetUnitNumber(next?.fleetUnitNumber ?? '');
        setProvinceId(next?.provinceId ?? '');
        setVinFilter(next?.vinFilter ?? '');

        const updatingParams = { ...invoiceListingDataParams };
        updatingParams.page = 1;
        updatingParams.yearData = next?.yearData ?? '';
        updatingParams.makeData = next?.makeData ?? '';
        updatingParams.modelData = next?.modelData ?? '';
        updatingParams.licenseNumber = next?.licenseNumber ?? '';
        updatingParams.fleetUnitNumber = next?.fleetUnitNumber ?? '';
        updatingParams.provinceId = next?.provinceId ?? '';
        updatingParams.vinFilter = next?.vinFilter ?? '';

        setPage(1);
        invoiceDetailsData(updatingParams);
        setOpen(false);
    };

    // const handleChangeRowsPerPage = (event) => {
    //   setRowsPerPage(parseInt(event.target.value, 10));
    //   setPage(0);
    // };
    const navigate = useNavigate();

    useEffect(() => {
        setInvoiceData({});
        const updatingParams = { ...invoiceListingDataParams };

        if (!search && localStorage.getItem('searchValue')) {
            updatingParams.search = localStorage.getItem('searchValue');
            setSearch(localStorage.getItem('searchValue'));
        }

        // updatingParams.page = 1;

        switch (name) {
            case 'all':
                setValue(0);
                updatingParams.currentPageStatus = 'all';
                break;
            case 'finalized':
                setValue(1);
                updatingParams.currentPageStatus = 'finalized';
                break;
            case 'sents':
                setValue(2);
                updatingParams.currentPageStatus = 'sent';
                break;
            case 'drafts':
                setValue(3);
                updatingParams.currentPageStatus = 'draft';
                break;
            default:
                setValue(0);
                updatingParams.currentPageStatus = 'all';
        }

        invoiceDetailsData(updatingParams);
    }, [window.location.pathname]);

    const invoiceDetailsData = async ({
        page,
        rowsPerPage,
        currentPageStatus,
        sortTypeName,
        sortByDate,
        licenseNumber,
        fleetUnitNumber,
        provinceId,
        vinFilter,
        yearData,
        makeData,
        modelData,
        dateData,
        search,
        paymentStatus,
    }) => {
        setShowLoader(true);
        let data = await dispatch(
            invoiceListing(
                page,
                rowsPerPage,
                currentPageStatus,
                sortTypeName,
                sortByDate,
                licenseNumber,
                fleetUnitNumber,
                yearData,
                makeData,
                modelData,
                dateData,
                search,
                null,
                paymentStatus,
                provinceId,
                vinFilter
            )
        );
        const id = data?._id;
        if (data?._id) {
            localStorage.setItem('id', id);
        }
        if (data) {
            setInvoiceData(data);
        }
        setTimeout(() => {
            setShowLoader(false);
        }, 100);
    };

    useEffect(() => {
        const updatingParams = { ...invoiceListingDataParams };
        updatingParams.page = 1;
        if (localStorage.getItem('searchValue')) {
            updatingParams.search = localStorage.getItem('searchValue');
            setSearch(localStorage.getItem('searchValue'));
        }
        setPage(1);
        invoiceDetailsData(updatingParams);
    }, [invoicePaymentStatus]);

    // useEffect(() => {
    //     return () => {
    //         // 🔹 Reset store when unmounting (leaving page)
    //         resetInvoiceStore();
    //     };
    // }, []);

    const handleSend = async (invoiceId) => {
        try {
            setShowLoader(true);
            const data = await dispatch(sendInvoice(invoiceId));
            if (data) {
                toast.success('Invoice was successfully emailed to customer');
            }
        } catch (error) {
            console.log('🚀 ~ handleSend ~ error:', error);
        } finally {
            setShowLoader(false);
        }
    };

    // const invoiceData = useSelector((state) => state.invoice.invoiceData);

    // const emptyRows =
    //   rowsPerPage - Math.min(rowsPerPage, invoiceData.length - page * rowsPerPage);

    const handleChangePage = (event, newPage) => {
        const updatingParams = { ...invoiceListingDataParams };

        updatingParams.page = newPage + 1;
        setPage(newPage + 1);
        // updatingParams.currentPageStatus = "all";

        invoiceDetailsData(updatingParams);
    };

    const handleChangeRowsPerPage = (event) => {
        console.log('event', event.target.value);
        const newRowsPerPage = event.target.value;
        setRowsPerPage(newRowsPerPage);

        // setRowsPerPage(parseInt(event.target.value, 5));
        //  setRowsPerPage(event.target.value);

        const updatingParams = {
            ...invoiceListingDataParams,

            rowsPerPage: event.target.value,
        };
        setPage(1);

        updatingParams.page = 1;
        // updatingParams.currentPageStatus = "all";

        invoiceDetailsData(updatingParams);
    };

    const handleSort = (sortType, sortBy) => {
        setsortByDate(sortBy);

        let newSortType;

        if (sortBy == 'createdAt') {
            newSortType = sortType == 'DSC' ? 'ASC' : 'DSC';
            setsortTypeDate(newSortType);
            setsortTypeName('');
            setsortTypeTotal('');
        } else if (sortBy == 'customer.fullName') {
            newSortType = sortType == 'DSC' ? 'ASC' : 'DSC';
            setsortTypeName(newSortType);
            setsortTypeDate('');
            setsortTypeTotal('');
        } else if (sortBy == 'Total') {
            newSortType = sortType == 'DSC' ? 'ASC' : 'DSC';
            setsortTypeName('');
            setsortTypeDate('');
            setsortTypeTotal(newSortType);
        }

        const updatingParams = {
            ...invoiceListingDataParams,
            page: 1,
            sortTypeName: newSortType,
            sortByDate: sortBy,
        };

        setPage(1);
        invoiceDetailsData(updatingParams);
    };

    const handleCancel = () => {
        // kept for existing callers; vehicle modal reset/apply is handled by handleVehicleApply now
        setYearData('');
        setMakeData('');
        setModelData('');
        setLicenseNumber('');
        setFleetUnitNumber('');
        setProvinceId('');
        setVinFilter('');
        setPage(1);

        const updatingParams = {
            ...invoiceListingDataParams,
            page: 1,
            modelData: '',
            yearData: '',
            makeData: '',
            licenseNumber: '',
            fleetUnitNumber: '',
            provinceId: '',
            vinFilter: '',
        };

        invoiceDetailsData(updatingParams);
        setOpen(false);
    };

    const handleDateChange = (date) => {
        const updatingParams = { ...invoiceListingDataParams };
        updatingParams.page = 1;
        setPage(1);

        if (date == null) {
            // clear both picker value and API filter
            setSelectDate(null);
            setDateData('');
            updatingParams.dateData = '';
            invoiceDetailsData(updatingParams);
            return;
        }

        // Guard: prevent API call for incomplete/invalid dates (e.g. typing "202" of "2026")
        const parsed = dayjs(date);
        if (!parsed.isValid() || parsed.year() < 1900 || parsed.year() > 2100) {
            return;
        }

        const utcDate = parsed.format('YYYY-MM-DD');
        setSelectDate(parsed);
        setDateData(utcDate);
        updatingParams.dateData = utcDate;
        invoiceDetailsData(updatingParams);
    };

    function debounce(func, wait) {
        let timeout;
        return function (...args) {
            const context = this;
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
                timeout = null;
                func.apply(context, args);
            }, wait);
        };
    }

    // Keep the debounced function stable, but always read latest params.
    const invoiceParamsRef = React.useRef(invoiceListingDataParams);
    useEffect(() => {
        invoiceParamsRef.current = invoiceListingDataParams;
    }, [invoiceListingDataParams]);

    const debounceOnChange = React.useMemo(
        () =>
            debounce((nextSearch) => {
                setSearch(nextSearch);
                const updatingParams = {
                    ...invoiceParamsRef.current,
                    page: 1,
                    search: nextSearch,
                };
                setPage(1);
                invoiceDetailsData(updatingParams);
            }, 800),
        []
    );

    const invoiceVehicleFieldCount = [
        licenseNumber?.trim?.(),
        fleetUnitNumber?.trim?.(),
        vinFilter?.trim?.(),
        yearData,
        makeData,
        modelData,
        provinceId,
    ].filter(Boolean).length;

    /** When status menu is pinned, payment selections are not cleared and do not count toward Clear. */
    const invoiceHasNonStatusClearable =
        Boolean(dateData) ||
        Boolean(yearData || makeData || modelData || licenseNumber?.trim?.() || fleetUnitNumber?.trim?.() || vinFilter?.trim?.() || provinceId) ||
        Boolean(search?.trim?.());
    const invoiceHasPaymentClearable = !isPinned && (invoicePaymentStatus?.length ?? 0) > 0;

    const invoiceHasClearableFilters = invoiceHasNonStatusClearable || invoiceHasPaymentClearable;

    const invoiceClearFilterGroups = [
        Boolean(dateData),
        Boolean(yearData || makeData || modelData || licenseNumber?.trim?.() || fleetUnitNumber?.trim?.() || vinFilter?.trim?.() || provinceId),
        Boolean(search?.trim?.()),
        !isPinned && (invoicePaymentStatus?.length ?? 0) > 0,
    ].filter(Boolean).length;
    const invoiceClearFilterLabel = invoiceClearFilterGroups > 1 ? 'Clear Filters' : 'Clear Filter';

    const handleClearInvoiceFilters = () => {
        setSelectDate(null);
        setDateData('');
        setYearData('');
        setMakeData('');
        setModelData('');
        setLicenseNumber('');
        setFleetUnitNumber('');
        setProvinceId('');
        setVinFilter('');
        setSearch('');
        localStorage.removeItem('searchValue');
        if (!isPinned) {
            setInvoicePaymentStatus([]);
        }

        // Also clear the DatePicker input text so no stale date string remains
        const dateInput = document.querySelector('#invoice-date-input');
        console.log('dateInput', dateInput);
        if (dateInput) {
            dateInput.value = '';
        }

        setPage(1);
        const updatingParams = {
            page: 1,
            rowsPerPage,
            currentPageStatus: name === 'sents' ? 'sent' : name === 'drafts' ? 'draft' : name === 'finalized' ? 'finalized' : 'all',
            sortTypeName: sortTypeName || sortTypeDate || sortTypeTotal || 'DSC',
            sortByDate: sortByDate,
            licenseNumber: '',
            fleetUnitNumber: '',
            provinceId: '',
            vinFilter: '',
            yearData: '',
            makeData: '',
            modelData: '',
            dateData: '',
            search: '',
            paymentStatus: isPinned ? invoicePaymentStatus : [],
        };
        invoiceDetailsData(updatingParams);
    };

    // if(showLoader){
    //   return <Loader showLoader={showLoader}/>
    // }

    return (
        <>
            <Box padding={'32px 24px'} sx={{ bgcolor: 'background.main' }}>
                <Grid container rowGap={3}>
                    <Grid container item justifyContent={'space-between'}>
                        <Grid item sm={4} lg={4}>
                            <Typography variant="h4" sx={{ fontWeight: 400 }}>
                                Invoices{' '}
                            </Typography>
                        </Grid>
                        <Grid container sm={8} lg={8} gap={3} justifyContent={'end'}>
                            <Grid item spacing={3}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleClickExport}
                                    endIcon={openExport ? <ArrowDropUp /> : <ArrowDropDown />}
                                >
                                    EXPORT
                                </Button>
                            </Grid>
                            {/* Dropdown Menu */}
                            <Menu
                                anchorEl={anchorEl}
                                open={openExport}
                                onClose={handleCloseExport}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <MenuItem onClick={handleInvoicesClick}>Invoices Sales Report</MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setOpenAccountExportModel(true);
                                        handleCloseExport();
                                    }}
                                >
                                    Accounts Receivable Report
                                </MenuItem>
                            </Menu>
                            <Grid item spacing={3}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        let obj = {
                                            ...recordPaymentParams,
                                            status: ['remaining'],
                                        };
                                        setRecordPaymentParams(obj);
                                        navigate('/invoice/record-payment');
                                    }}
                                >
                                    Record Payment
                                </Button>
                            </Grid>
                            <Grid>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<Add />}
                                    onClick={() => {
                                        navigate('/invoice/new');
                                    }}
                                    className="btn-text-size-manage"
                                >
                                    NEW Invoice
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        item
                        // gap={2}
                        margin={'12px 0'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Grid item container alignItems={'center'}>
                            <Box sx={{ width: '100%' }}>
                                <Box>
                                    <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                                        <Tab label="ALL" {...a11yProps(0)} style={{ fontWeight: 500 }} />
                                        <Tab label="Finalized" {...a11yProps(1)} style={{ fontWeight: 500 }} />
                                        <Tab label="Sent" {...a11yProps(2)} style={{ fontWeight: 500 }} />
                                        <Tab label="Drafts" {...a11yProps(3)} style={{ fontWeight: 500 }} />
                                    </Tabs>
                                </Box>
                                {index?.map((i) => {
                                    return (
                                        <CustomTabPanel value={value} key={i} index={i} padding={'24px'}>
                                            <Grid container item>
                                                <Grid container marginBottom={'32px'} alignItems={'center'} justifyContent={'space-between'}>
                                                    {/* Select Date */}
                                                    <Grid container xs={12} sm={6} md={8} justifyContent={'flex-start'} alignItems={'center'}>
                                                        <Grid item xs="auto" padding={'6px'}>
                                                            <Stack
                                                                direction={'row'}
                                                                alignItems={'center'}
                                                                display={'flex'}
                                                                justifyContent={'space-between'}
                                                                spacing={1}
                                                            >
                                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                    <DatePicker
                                                                        label="Select date"
                                                                        name="proposeDate"
                                                                        value={selectDate}
                                                                        onChange={handleDateChange}
                                                                        sx={{ width: 240, minWidth: 240, maxWidth: 240 }}
                                                                        slotProps={{
                                                                            textField: {
                                                                                fullWidth: true,
                                                                                sx: {
                                                                                    width: 240,
                                                                                    minWidth: 240,
                                                                                    maxWidth: 240,
                                                                                    '& .MuiInputBase-root': { width: '100%' },
                                                                                },
                                                                                inputProps: {
                                                                                    id: 'invoice-date-input',
                                                                                },
                                                                            },
                                                                            field: { clearable: true },
                                                                        }}
                                                                    />
                                                                </LocalizationProvider>
                                                            </Stack>
                                                        </Grid>

                                                        {/* Filter by vehicle */}
                                                        <Grid item xs="auto" padding={'6px'}>
                                                            <Button
                                                                variant={invoiceVehicleFieldCount > 0 ? 'contained' : 'text'}
                                                                color="primary"
                                                                onClick={handleOpen}
                                                                sx={{
                                                                    color: invoiceVehicleFieldCount > 0 ? 'white!important' : 'black!important',
                                                                    fontWeight: 500,
                                                                    lineHeight: '24px',
                                                                    padding: '6px 12px!important',
                                                                    height: '40px',
                                                                    // height: '56px!important',
                                                                }}
                                                                startIcon={
                                                                    invoiceVehicleFieldCount > 0 && (
                                                                        <Avatar
                                                                            sx={{
                                                                                bgcolor: 'white',
                                                                                color: 'primary.main',
                                                                                fontSize: '14px!important',
                                                                                fontWeight: '500!important',
                                                                                letterSpacing: '0.4px',
                                                                                lineHeight: '24px',
                                                                                width: 24,
                                                                                height: 24,
                                                                            }}
                                                                        >
                                                                            {invoiceVehicleFieldCount}
                                                                        </Avatar>
                                                                    )
                                                                }
                                                                endIcon={<ExpandMoreIcon />}
                                                            >
                                                                FILTER BY VEHICLE
                                                            </Button>
                                                            <SelectVehicleModal
                                                                open={open}
                                                                onCancel={handleVehicleCancel}
                                                                onApply={handleVehicleApply}
                                                                yearData={yearData}
                                                                makeData={makeData}
                                                                modelData={modelData}
                                                                licenseNumber={licenseNumber}
                                                                fleetUnitNumber={fleetUnitNumber}
                                                                provinceId={provinceId}
                                                                vinFilter={vinFilter}
                                                            />
                                                        </Grid>

                                                        {/* Status Filter */}
                                                        <Grid item xs="auto" padding={'6px'}>
                                                            <Button
                                                                variant={(invoicePaymentStatus?.length ?? 0) > 0 || isPinned ? 'contained' : 'text'}
                                                                id="status-filter-button"
                                                                aria-haspopup="true"
                                                                onClick={(event) => setAnchorElStatus(event.currentTarget)}
                                                                sx={{
                                                                    color:
                                                                        (invoicePaymentStatus?.length ?? 0) > 0 || isPinned
                                                                            ? 'white!important'
                                                                            : 'black!important',
                                                                    fontWeight: 500,
                                                                    lineHeight: '24px',
                                                                    padding: '6px 12px!important',
                                                                    height: '40px',
                                                                    // height: '56px!important',
                                                                }}
                                                                startIcon={
                                                                    invoicePaymentStatus?.length > 0 && (
                                                                        <Avatar
                                                                            sx={{
                                                                                bgcolor: 'white',
                                                                                color: 'primary.main',
                                                                                fontSize: '14px!important',
                                                                                fontWeight: '500!important',
                                                                                letterSpacing: '0.4px',
                                                                                lineHeight: '24px',
                                                                                width: 24,
                                                                                height: 24,
                                                                            }}
                                                                        >
                                                                            {invoicePaymentStatus?.length}
                                                                        </Avatar>
                                                                    )
                                                                }
                                                                endIcon={
                                                                    <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                                                                        <ExpandMore />
                                                                        {(invoicePaymentStatus?.length > 0 || isPinned) && (
                                                                            <IconButton
                                                                                onClick={(event) => {
                                                                                    event.preventDefault();
                                                                                    event.stopPropagation();
                                                                                    setPinned(!isPinned); // toggle pin
                                                                                }}
                                                                                sx={{
                                                                                    color:
                                                                                        (invoicePaymentStatus?.length ?? 0) > 0 || isPinned
                                                                                            ? 'white'
                                                                                            : 'rgba(0,0,0,0.54)',
                                                                                    width: '26px',
                                                                                    height: '26px',
                                                                                    background: isPinned ? '#003267' : 'rgba(0,0,0,0.20)',
                                                                                }}
                                                                            >
                                                                                {isPinned ? (
                                                                                    <PushPin
                                                                                        sx={{
                                                                                            transform: 'revert',
                                                                                            fontSize: 16,
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <PushPinOutlined
                                                                                        sx={{
                                                                                            transform: 'rotate(45deg)',

                                                                                            fontSize: 16,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </IconButton>
                                                                        )}
                                                                    </Stack>
                                                                }
                                                            >
                                                                STATUS
                                                            </Button>

                                                            <Menu
                                                                id="basic-menu"
                                                                anchorEl={anchorElStatus}
                                                                open={openStatus}
                                                                onClose={() => setAnchorElStatus(null)}
                                                                MenuListProps={{
                                                                    'aria-labelledby': 'status-filter-button',
                                                                }}
                                                                PaperProps={{
                                                                    style: {
                                                                        maxHeight: '400px',
                                                                    },
                                                                }}
                                                            >
                                                                {[
                                                                    { _id: 'fullyPaid', label: 'Paid' },
                                                                    { _id: 'remaining', label: 'Remaining' },
                                                                    { _id: 'owing', label: 'Owing' },
                                                                ].map((status) => (
                                                                    <MenuItem key={status._id} sx={styles.menuPadding}>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={invoicePaymentStatus?.includes(status._id) || false}
                                                                                    onChange={() => {
                                                                                        const currentStatus = invoicePaymentStatus || [];
                                                                                        let updatedStatus;

                                                                                        if (currentStatus.includes(status._id)) {
                                                                                            updatedStatus = currentStatus.filter((id) => id !== status._id);
                                                                                        } else {
                                                                                            updatedStatus = [...currentStatus, status._id];
                                                                                        }
                                                                                        setInvoicePaymentStatus(updatedStatus);
                                                                                    }}
                                                                                />
                                                                            }
                                                                            label={status.label}
                                                                            sx={styles.formControlLabel}
                                                                        />
                                                                    </MenuItem>
                                                                ))}
                                                            </Menu>
                                                        </Grid>

                                                        {invoiceHasClearableFilters && (
                                                            <Grid item xs="auto" padding={'6px'}>
                                                                <Button
                                                                    variant="contained"
                                                                    color="secondary"
                                                                    startIcon={<Close />}
                                                                    onClick={handleClearInvoiceFilters}
                                                                    sx={{
                                                                        background: '#0000001A',
                                                                        color: '#000000',
                                                                        '&:hover': {
                                                                            background: '#0000001A',
                                                                            boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                                                        },
                                                                        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                                                        fontWeight: 500,
                                                                        lineHeight: '24px',
                                                                        // height: '56px!important',
                                                                    }}
                                                                >
                                                                    {invoiceClearFilterLabel}
                                                                </Button>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                    <Grid container xs={12} sm={4} md={4} justifyContent={'flex-end'} padding={'6px'}>
                                                        {/* Select Search */}
                                                        <FormControl fullWidth>
                                                            <InputLabel htmlFor="outlined-adornment-search">Search</InputLabel>
                                                            <OutlinedInput
                                                                id="outlined-adornment-search"
                                                                placeholder="Customer name, service, etc..."
                                                                startAdornment={
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon />
                                                                    </InputAdornment>
                                                                }
                                                                value={search}
                                                                label="Search"
                                                                onChange={(e) => {
                                                                    setSearch(e.target.value);
                                                                    localStorage.setItem('searchValue', e.target.value);
                                                                    debounceOnChange(e.target.value);
                                                                }}
                                                            />
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                                <Grid container item padding={'24px'} boxShadow={2} sx={{ bgcolor: 'white' }}>
                                                    {invoiceData?.data?.length == 0 && showLoader == false ? (
                                                        <div className="not-found-class">Sorry, we could not find any matches.</div>
                                                    ) : (
                                                        <Grid item xl={12} md={12} sm={12} xs={12}>
                                                            <TableContainer sx={{ maxHeight: 600, width: '100%' }}>
                                                                <Table
                                                                    stickyHeader
                                                                    aria-label="caption table"
                                                                    padding="16px"
                                                                    sx={INVOICE_LIST_TABLE_SX}
                                                                    style={{ boxShadow: 0 }}
                                                                >
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell align="left" sx={INVOICE_LIST_COL}>
                                                                                <TableSortLabel
                                                                                    active={true}
                                                                                    direction={sortTypeDate == 'ASC' ? 'asc' : 'desc'}
                                                                                    onClick={() => {
                                                                                        setsortByDate('createdAt');
                                                                                        handleSort(sortTypeDate || 'DSC', 'createdAt');
                                                                                    }}
                                                                                    sx={{ fontWeight: 500 }}
                                                                                >
                                                                                    Invoice Number
                                                                                </TableSortLabel>{' '}
                                                                            </TableCell>
                                                                            <TableCell align="left" sx={INVOICE_LIST_COL}>
                                                                                <TableSortLabel
                                                                                    active={true}
                                                                                    direction={sortTypeDate == 'ASC' ? 'asc' : 'desc'}
                                                                                    onClick={() => {
                                                                                        handleSort(sortTypeDate || 'DSC', 'createdAt');
                                                                                    }}
                                                                                    sx={{ fontWeight: 500 }}
                                                                                >
                                                                                    Date
                                                                                </TableSortLabel>
                                                                            </TableCell>
                                                                            <TableCell align="left" sx={{ ...INVOICE_LIST_COL }}>
                                                                                <TableSortLabel
                                                                                    active={true}
                                                                                    direction={sortTypeName == 'ASC' ? 'asc' : 'desc'}
                                                                                    onClick={() => {
                                                                                        handleSort(sortTypeName || 'DSC', 'customer.fullName');
                                                                                    }}
                                                                                    sx={{ fontWeight: 500 }}
                                                                                >
                                                                                    Customer Name
                                                                                </TableSortLabel>
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{ ...INVOICE_LIST_COL, fontWeight: 500, minWidth: 160 }}
                                                                            >
                                                                                Vehicle
                                                                            </TableCell>
                                                                            {showInvoiceLicensePlateColumn && (
                                                                                <TableCell align="left" sx={{ ...INVOICE_LIST_COL, fontWeight: 500 }}>
                                                                                    Licence Plate
                                                                                </TableCell>
                                                                            )}
                                                                            {showInvoiceUnitColumn && (
                                                                                <TableCell align="left" sx={{ ...INVOICE_LIST_COL, fontWeight: 500 }}>
                                                                                    Unit #
                                                                                </TableCell>
                                                                            )}

                                                                            <TableCell align="left" sx={INVOICE_LIST_COL}>
                                                                                <TableSortLabel
                                                                                    active={true}
                                                                                    direction={sortTypeTotal == 'ASC' ? 'asc' : 'desc'}
                                                                                    onClick={() => {
                                                                                        handleSort(sortTypeTotal || 'DSC', 'Total');
                                                                                    }}
                                                                                    sx={{ fontWeight: 500 }}
                                                                                >
                                                                                    Total
                                                                                </TableSortLabel>
                                                                            </TableCell>

                                                                            <TableCell align="left" sx={{ ...INVOICE_LIST_COL, fontWeight: 500 }}>
                                                                                Invoice Status
                                                                            </TableCell>

                                                                            <TableCell align="left" sx={{ ...INVOICE_LIST_COL, fontWeight: 500 }}>
                                                                                Payment Status
                                                                            </TableCell>

                                                                            <TableCell
                                                                                align="left"
                                                                                sx={{
                                                                                    ...INVOICE_LIST_COL,
                                                                                    fontWeight: 500,
                                                                                }}
                                                                            >
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={handleInvoiceColumnSettingsClick}
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        width: 32,
                                                                                        height: 32,
                                                                                        justifyContent: 'center',
                                                                                        alignItems: 'center',
                                                                                        gap: '12px',
                                                                                        flexShrink: 0,
                                                                                        aspectRatio: '1 / 1',

                                                                                        borderRadius: '4px',
                                                                                        border: openInvoiceColumnSettings
                                                                                            ? '1px solid rgba(0, 0, 0, 0.10)'
                                                                                            : 'none',

                                                                                        backgroundColor: openInvoiceColumnSettings ? '#EBEBEB' : 'transparent',
                                                                                        p: 0,

                                                                                        '&:hover': {
                                                                                            backgroundColor: openInvoiceColumnSettings
                                                                                                ? '#EBEBEB'
                                                                                                : 'transparent',
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <svg
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        width="20"
                                                                                        height="20"
                                                                                        viewBox="0 0 20 20"
                                                                                        fill="none"
                                                                                    >
                                                                                        <path
                                                                                            d="M12.4604 1.66699H7.53932V3.81826L5.96211 4.74505L4.12706 3.66859L1.6665 7.99873L3.5011 9.07491L3.50111 10.9258L1.66659 12.0019L4.12715 16.3321L5.96215 15.2557L7.53932 16.1824V18.3337H12.4604V16.1824L14.0376 15.2557L15.8725 16.3321L18.3331 12.0019L16.4987 10.9258V9.07482L18.3332 7.99873L15.8726 3.66859L14.0376 4.74502L12.4604 3.81826V1.66699Z"
                                                                                            stroke={!openInvoiceColumnSettings ? '#8E8E8E' : 'black'}
                                                                                            strokeWidth="1.25"
                                                                                            strokeLinejoin="round"
                                                                                        />
                                                                                        <path
                                                                                            d="M12.9168 9.99967C12.9168 11.6105 11.611 12.9163 10.0002 12.9163C8.38933 12.9163 7.0835 11.6105 7.0835 9.99967C7.0835 8.38884 8.38933 7.08301 10.0002 7.08301C11.611 7.08301 12.9168 8.38884 12.9168 9.99967Z"
                                                                                            stroke={!openInvoiceColumnSettings ? '#8E8E8E' : 'black'}
                                                                                            strokeWidth="1.25"
                                                                                        />
                                                                                    </svg>
                                                                                </IconButton>
                                                                            </TableCell>

                                                                            <TableCell
                                                                                align="right"
                                                                                sx={{ ...INVOICE_LIST_ACTION_COL, paddingRight: 1 }}
                                                                                aria-label="Actions"
                                                                            />
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {Array.isArray(invoiceData?.data) &&
                                                                            invoiceData?.data?.map((row) => (
                                                                                <TableRow key={row._id} sx={{ cursor: 'pointer' }} hover={true}>
                                                                                    <TableCell
                                                                                        component="th"
                                                                                        scope="row"
                                                                                        align="left"
                                                                                        sx={INVOICE_LIST_BODY_CELL}
                                                                                        onClick={() => {
                                                                                            let status;

                                                                                            if (row?.status === 'draft') {
                                                                                                status = 'draft';
                                                                                            } else if (row?.status === 'sent') {
                                                                                                status = row.isEmailSent ? 'sent' : 'finalized';
                                                                                            } else {
                                                                                                status = row.status;
                                                                                            }

                                                                                            navigate(`/invoice/${status}/${row?._id}`);
                                                                                        }}
                                                                                    >
                                                                                        {row?.invoiceNumber}
                                                                                    </TableCell>
                                                                                    <TableCell
                                                                                        align="left"
                                                                                        sx={INVOICE_LIST_BODY_CELL}
                                                                                        onClick={() => {
                                                                                            let status;

                                                                                            if (row?.status === 'draft') {
                                                                                                status = 'draft';
                                                                                            } else if (row?.status === 'sent') {
                                                                                                status = row.isEmailSent ? 'sent' : 'finalized';
                                                                                            } else {
                                                                                                status = row.status;
                                                                                            }

                                                                                            navigate(`/invoice/${status}/${row?._id}`);
                                                                                        }}
                                                                                    >
                                                                                        {row?.invoiceDate
                                                                                            ? dayjs(row?.invoiceDate).tz(USER_TIMEZONE).format('MMMM D, YYYY')
                                                                                            : moment(row && row?.createdAt).format('MMMM D, YYYY')}
                                                                                    </TableCell>
                                                                                    <TableCell
                                                                                        align="left"
                                                                                        sx={INVOICE_LIST_BODY_CELL}
                                                                                        onClick={() => {
                                                                                            let status;

                                                                                            if (row?.status === 'draft') {
                                                                                                status = 'draft';
                                                                                            } else if (row?.status === 'sent') {
                                                                                                status = row.isEmailSent ? 'sent' : 'finalized';
                                                                                            } else {
                                                                                                status = row.status;
                                                                                            }

                                                                                            navigate(`/invoice/${status}/${row?._id}`);
                                                                                        }}
                                                                                    >
                                                                                        {`${row?.customer?.fullName}`}
                                                                                    </TableCell>
                                                                                    <TableCell
                                                                                        align="left"
                                                                                        sx={INVOICE_LIST_BODY_CELL}
                                                                                        onClick={() => {
                                                                                            let status;

                                                                                            if (row?.status === 'draft') {
                                                                                                status = 'draft';
                                                                                            } else if (row?.status === 'sent') {
                                                                                                status = row.isEmailSent ? 'sent' : 'finalized';
                                                                                            } else {
                                                                                                status = row.status;
                                                                                            }

                                                                                            navigate(`/invoice/${status}/${row?._id}`);
                                                                                        }}
                                                                                    >
                                                                                        {formatInvoiceVehicleDisplay(row?.vehicle)}
                                                                                    </TableCell>
                                                                                    {showInvoiceLicensePlateColumn && (
                                                                                        <TableCell
                                                                                            align="left"
                                                                                            sx={INVOICE_LIST_BODY_CELL}
                                                                                            onClick={() => {
                                                                                                let status;

                                                                                                if (row?.status === 'draft') {
                                                                                                    status = 'draft';
                                                                                                } else if (row?.status === 'sent') {
                                                                                                    status = row.isEmailSent ? 'sent' : 'finalized';
                                                                                                } else {
                                                                                                    status = row.status;
                                                                                                }

                                                                                                navigate(`/invoice/${status}/${row?._id}`);
                                                                                            }}
                                                                                        >
                                                                                            {formatInvoiceLicensePlateDisplay(row?.vehicle)}
                                                                                        </TableCell>
                                                                                    )}
                                                                                    {showInvoiceUnitColumn && (
                                                                                        <TableCell
                                                                                            align="left"
                                                                                            sx={INVOICE_LIST_BODY_CELL}
                                                                                            onClick={() => {
                                                                                                let status;

                                                                                                if (row?.status === 'draft') {
                                                                                                    status = 'draft';
                                                                                                } else if (row?.status === 'sent') {
                                                                                                    status = row.isEmailSent ? 'sent' : 'finalized';
                                                                                                } else {
                                                                                                    status = row.status;
                                                                                                }

                                                                                                navigate(`/invoice/${status}/${row?._id}`);
                                                                                            }}
                                                                                        >
                                                                                            {row?.vehicle?.fleetUnitNumber
                                                                                                ? String(row.vehicle.fleetUnitNumber)
                                                                                                : '—'}
                                                                                        </TableCell>
                                                                                    )}

                                                                                    <TableCell
                                                                                        align="left"
                                                                                        sx={INVOICE_LIST_BODY_CELL}
                                                                                        onClick={() => {
                                                                                            let status;

                                                                                            if (row?.status === 'draft') {
                                                                                                status = 'draft';
                                                                                            } else if (row?.status === 'sent') {
                                                                                                status = row.isEmailSent ? 'sent' : 'finalized';
                                                                                            } else {
                                                                                                status = row.status;
                                                                                            }

                                                                                            navigate(`/invoice/${status}/${row?._id}`);
                                                                                        }}
                                                                                    >
                                                                                        {row?.Total
                                                                                            ? NumericEngine.format(row.Total, {
                                                                                                  formatCurrency: true,
                                                                                                  trailingZeros: true,
                                                                                              })
                                                                                            : ''}
                                                                                    </TableCell>

                                                                                    <TableCell
                                                                                        align="left"
                                                                                        sx={INVOICE_LIST_BODY_CELL}
                                                                                        onClick={() => {
                                                                                            let status;

                                                                                            if (row?.status === 'draft') {
                                                                                                status = 'draft';
                                                                                            } else if (row?.status === 'sent') {
                                                                                                status = row.isEmailSent ? 'sent' : 'finalized';
                                                                                            } else {
                                                                                                status = row.status;
                                                                                            }

                                                                                            navigate(`/invoice/${status}/${row?._id}`);
                                                                                        }}
                                                                                    >
                                                                                        <Chip
                                                                                            label={
                                                                                                row?.status === 'draft'
                                                                                                    ? 'Draft'
                                                                                                    : row?.isEmailSent
                                                                                                      ? 'Sent'
                                                                                                      : 'Finalized'
                                                                                            }
                                                                                            style={{
                                                                                                backgroundColor:
                                                                                                    row?.status === 'draft'
                                                                                                        ? CUSTOOMER_INVOICE_STATUS_COLOR.DRAFT
                                                                                                        : row?.isEmailSent
                                                                                                          ? CUSTOOMER_INVOICE_STATUS_COLOR.SENT
                                                                                                          : CUSTOOMER_INVOICE_STATUS_COLOR.FINALIZED,
                                                                                                color: 'rgba(0,0,0,0.87)',
                                                                                                fontWeight: '500',
                                                                                                fontWeight: '500',
                                                                                                lineHeight: '18px',
                                                                                                letterSpacing: '0.16px',
                                                                                            }}
                                                                                        />
                                                                                    </TableCell>

                                                                                    <TableCell
                                                                                        align="left"
                                                                                        sx={INVOICE_LIST_BODY_CELL}
                                                                                        onClick={() => {
                                                                                            let status;

                                                                                            if (row?.status === 'draft') {
                                                                                                status = 'draft';
                                                                                            } else if (row?.status === 'sent') {
                                                                                                status = row.isEmailSent ? 'sent' : 'finalized';
                                                                                            } else {
                                                                                                status = row.status;
                                                                                            }

                                                                                            navigate(`/invoice/${status}/${row?._id}`);
                                                                                        }}
                                                                                    >
                                                                                        {row?.status === 'draft' || !row.invoicePaymentStatus ? (
                                                                                            <></>
                                                                                        ) : (
                                                                                            <Chip
                                                                                                label={
                                                                                                    row.invoicePaymentStatus ===
                                                                                                        INVOICE_PAYMENT_STATUS.PARTIALLY_PAID ||
                                                                                                    row.invoicePaymentStatus === INVOICE_PAYMENT_STATUS.UNPAID
                                                                                                        ? 'Remaining'
                                                                                                        : row.invoicePaymentStatus ===
                                                                                                            INVOICE_PAYMENT_STATUS.FULLY_PAID
                                                                                                          ? 'Fully Paid'
                                                                                                          : row.invoicePaymentStatus ===
                                                                                                              INVOICE_PAYMENT_STATUS.OVER_PAID
                                                                                                            ? 'Owing'
                                                                                                            : 'Remaining'
                                                                                                }
                                                                                                style={{
                                                                                                    backgroundColor:
                                                                                                        row.invoicePaymentStatus ===
                                                                                                            INVOICE_PAYMENT_STATUS.PARTIALLY_PAID ||
                                                                                                        row.invoicePaymentStatus ===
                                                                                                            INVOICE_PAYMENT_STATUS.UNPAID
                                                                                                            ? CHIP_COLOR.BALANCE_REMAINING
                                                                                                            : row.invoicePaymentStatus ===
                                                                                                                INVOICE_PAYMENT_STATUS.FULLY_PAID
                                                                                                              ? CHIP_COLOR.FULLY_PAID
                                                                                                              : row.invoicePaymentStatus ===
                                                                                                                  INVOICE_PAYMENT_STATUS.OVER_PAID
                                                                                                                ? CHIP_COLOR.BALANCE_OWING
                                                                                                                : CHIP_COLOR.BALANCE_REMAINING,

                                                                                                    color:
                                                                                                        row.invoicePaymentStatus ===
                                                                                                            INVOICE_PAYMENT_STATUS.PARTIALLY_PAID ||
                                                                                                        row.invoicePaymentStatus ===
                                                                                                            INVOICE_PAYMENT_STATUS.UNPAID
                                                                                                            ? CHIP_COLOR.DEFAULT_COLOR
                                                                                                            : row.invoicePaymentStatus ===
                                                                                                                INVOICE_PAYMENT_STATUS.FULLY_PAID
                                                                                                              ? CHIP_COLOR.FULLY_PAID_COLOR
                                                                                                              : row.invoicePaymentStatus ===
                                                                                                                  INVOICE_PAYMENT_STATUS.OVER_PAID
                                                                                                                ? CHIP_COLOR.BALANCE_OWING_COLOR
                                                                                                                : CHIP_COLOR.DEFAULT_COLOR,

                                                                                                    display: 'flex',
                                                                                                    borderRadius: '100px',
                                                                                                    fontSize: '13px',
                                                                                                    fontWeight: '500',
                                                                                                    lineHeight: '18px',
                                                                                                    letterSpacing: '0.16px',
                                                                                                    width: 'max-content',
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    </TableCell>

                                                                                    <TableCell
                                                                                        align="right"
                                                                                        sx={{ ...INVOICE_LIST_BODY_CELL, ...INVOICE_LIST_ACTION_COL }}
                                                                                    >
                                                                                        {row?.status && (
                                                                                            <Box display="flex" justifyContent="flex-end">
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    sx={{
                                                                                                        background: '#EBEBEB',
                                                                                                        width: '24px',
                                                                                                        height: '24px',
                                                                                                    }}
                                                                                                    onClick={(event) => {
                                                                                                        event.stopPropagation();
                                                                                                        handleMenuOpen(event, row);
                                                                                                    }}
                                                                                                >
                                                                                                    <MoreVert
                                                                                                        sx={{
                                                                                                            width: '18px',
                                                                                                            height: '18px',
                                                                                                            display: 'flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center',
                                                                                                        }}
                                                                                                    />
                                                                                                </IconButton>
                                                                                            </Box>
                                                                                        )}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                            <Grid container item alignItems={'center'} justifyContent="end">
                                                                <Grid item>
                                                                    {showLoader ? (
                                                                        ''
                                                                    ) : (
                                                                        <>
                                                                            <TablePagination
                                                                                rowsPerPageOptions={[
                                                                                    5, 10, 25,
                                                                                    // { label: "All", value: 1000 },
                                                                                ]}
                                                                                colSpan={3}
                                                                                count={invoiceData?.totalCount}
                                                                                rowsPerPage={rowsPerPage}
                                                                                page={page > 0 ? page - 1 : 0}
                                                                                slotProps={{
                                                                                    select: {
                                                                                        'aria-label': 'rows per page',
                                                                                    },
                                                                                    actions: {
                                                                                        showFirstButton: true,
                                                                                        showLastButton: true,
                                                                                    },
                                                                                }}
                                                                                onPageChange={handleChangePage}
                                                                                onRowsPerPageChange={handleChangeRowsPerPage}
                                                                            />
                                                                        </>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    )}
                                                </Grid>{' '}
                                            </Grid>
                                        </CustomTabPanel>
                                    );
                                })}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <ManageColumnsMenu
                anchorEl={invoiceColumnAnchorEl}
                open={openInvoiceColumnSettings}
                onClose={handleInvoiceColumnSettingsClose}
                columns={[
                    { key: 'licensePlate', label: 'Licence Plate', checked: showInvoiceLicensePlateColumn, onToggle: setShowInvoiceLicensePlateColumn },
                    { key: 'unit', label: 'Unit #', checked: showInvoiceUnitColumn, onToggle: setShowInvoiceUnitColumn },
                ]}
            />

            <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorElDropdown}
                open={openDropdown}
                onClose={handleMenuClose}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '4px!important',
                        boxShadow: '0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 5px 5px -3px rgba(0, 0, 0, 0.20)',
                    },
                }}
            >
                {selectedRow?.status !== 'draft' && (
                    <MenuItem
                        onClick={() => {
                            clickDownload(selectedRow);
                            handleMenuClose();
                        }}
                    >
                        <Download />
                        <span style={{ marginLeft: '10px' }}>Download PDF</span>
                    </MenuItem>
                )}

                {selectedRow?.status === 'draft' && selectedRow?.pdfUrl && (
                    <MenuItem
                        onClick={() => {
                            clickDownload(selectedRow);
                            handleMenuClose();
                        }}
                    >
                        <Download />
                        <span style={{ marginLeft: '10px' }}>Download PDF</span>
                    </MenuItem>
                )}

                <MenuItem
                    onClick={() => {
                        if (!selectedRow) return;
                        navigate(`/customers/${selectedRow?.customer?._id}/${selectedRow?.customer?.createdFrom}`);
                        handleMenuClose();
                    }}
                >
                    <Visibility />
                    <span style={{ marginLeft: '10px' }}>View Customer</span>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        if (!selectedRow) return;
                        navigate(`/invoice/edit/${selectedRow?._id}`);
                        handleMenuClose();
                    }}
                >
                    <Edit style={{ marginRight: '10px' }} /> Edit Invoice
                </MenuItem>

                {/* {selectedRow?.status !== 'draft' && ( */}
                <MenuItem
                    onClick={() => {
                        handleSend(selectedRow?._id);
                        handleMenuClose();
                    }}
                >
                    <ResendSvg />
                    <span style={{ marginLeft: '10px' }}>{selectedRow?.isEmailSent ? 'Resend' : 'Send'}</span>
                </MenuItem>
                {/* )} */}

                {/* {selectedRow?.status !== 'draft' && (
                    <MenuItem
                        onClick={() => {
                            let obj = {
                                ...recordPaymentParams,
                                status: ['remaining'],
                            };
                            setRecordPaymentParams(obj);
                            navigate('/invoice/record-payment');
                            handleMenuClose();
                        }}
                    >
                        <RecordPaymentSvg />
                        <span style={{ marginLeft: '10px' }}>
                            Record Payment
                        </span>
                    </MenuItem>
                )} */}

                <MenuItem
                    onClick={async () => {
                        setIsDeleteModal(true);
                        setAnchorElDropdown(null);
                    }}
                >
                    <Delete sx={{ color: '#D32F2F' }} />
                    <span style={{ marginLeft: '10px', color: '#D32F2F' }}>Delete Invoice</span>
                </MenuItem>
            </Menu>
            <CommonDialog
                loader={deleteLoader}
                open={isDeleteModal}
                onClose={() => {
                    setIsDeleteModal(false);
                }}
                onSave={async () => {
                    setDeleteLoader(true);
                    try {
                        if (selectedRow?.status !== 'draft') {
                            await deleteFinalizedInvoice(selectedRow?._id);
                            queryClient.invalidateQueries({ queryKey: businessInvoicesKeys.all, refetchType: 'all' });
                            queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'], refetchType: 'all' });
                        } else {
                            queryClient.invalidateQueries({ queryKey: businessInvoicesKeys.all, refetchType: 'all' });
                            queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'], refetchType: 'all' });
                            await dispatch(deleteInvoice(selectedRow?._id));
                        }
                    } catch (error) {
                    } finally {
                        const updatingParams = { ...invoiceListingDataParams };

                        updatingParams.page = page;
                        invoiceDetailsData(updatingParams);
                        setSelectedRow(null);
                        setIsDeleteModal(false);
                        setDeleteLoader(false);
                    }
                }}
                dialogTitle={'Delete Invoice?'}
                dialogSubtitle={`Are you sure you want to delete Invoice ${selectedRow?.invoiceNumber || ''}? This action cannot be undone.`}
            />
            <ExportReportModel open={openExportModel} close={setOpenExportModel} />
            <AccountsReceivableReportModel open={openAccountExportModel} close={setOpenAccountExportModel} />
            <Dialog fullScreen={false} open={showLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Invoice;
